/**
 * Centralized example data for all tabs.
 *
 * All mode-dependent (arrival/departure) and shift-dependent (morning/night)
 * example values live here for easy editing.
 */

export type ShiftType = "morning" | "night";
export type OperationMode = "arrival" | "departure";

// ---------------------------------------------------------------------------
// Generate Tab — reported officer counts
// ---------------------------------------------------------------------------

export const REPORTED_COUNT: Record<string, number> = {
  "morning-arrival": 18,
  "morning-departure": 18,
  "night-arrival": 22,
  "night-departure": 22,
};

// ---------------------------------------------------------------------------
// Generate Tab — adhoc (handwritten/GL) counter examples by S/N
// Counter numbers differ per operation mode and shift type.
// ---------------------------------------------------------------------------

export const ADHOC_EXAMPLES: Record<string, Record<number, string>> = {
  "morning-arrival": {
    3: "5",   // HW
    4: "1",   // GL
    7: "12",  // HW
    8: "11",  // GL
    12: "20", // GL
    16: "39", // GL
  },
  "morning-departure": {
    3: "3",   // HW
    4: "1",   // GL
    7: "10",  // HW
    8: "9",   // GL
    12: "18", // GL
    16: "35", // GL
  },
  "night-arrival": {
    1: "10", 
    5: "9",   
    9: "16",  
    13: "26",   
    21: "15", 
  },
  "night-departure": {
    17: "32",
  },
};

// ---------------------------------------------------------------------------
// Generate Tab — RO/RA examples by shift
// Stored directly per shift (no runtime +12h transformation needed).
// ---------------------------------------------------------------------------

export const MORNING_RORA_EXAMPLES: Record<number, string> = {
  3: "RO2100",
  11: "RO1700",
  15: "RA1130",
};

export const NIGHT_RORA_EXAMPLES: Record<number, string> = {
  3: "RO0900",
  11: "RO0700",
  15: "RA2330",
};

// ---------------------------------------------------------------------------
// Generate Tab — example names as they appear in the PDF, to show the different
// ways an RA/RO and excluded time ranges ("HHMM-HHMM" + optional free text) get
// auto-extracted out of the name. Keyed by S/N.
// ---------------------------------------------------------------------------

export const MORNING_NAME_EXAMPLES: Record<number, string> = {
  2: "Putri (RA1100) 1200-1300 engagement at iB room", // RA + range + free text
  6: "Adam (1100-1200 training)",                       // range inside brackets
  10: "Sierra - 1400-1600 filming",                     // range after a dash
  14: "Ben 1000-1030 medical 1500-1545 course",         // more than one range
};

export const NIGHT_NAME_EXAMPLES: Record<number, string> = {
  2: "Putri (RA2300) 0000-0100 engagement at iB room",
  6: "Adam (0100-0200 training)",
  10: "Sierra - 0400-0600 filming",
  14: "Ben 2200-2230 medical 0300-0345 course",
};

// ---------------------------------------------------------------------------
// Add SOS Tab — example paste text
// ---------------------------------------------------------------------------

export function getSOSExampleText(mode: OperationMode, shift: ShiftType): string {
  const modeLabel = mode === "arrival" ? "ACAR" : "DCAR";
  const shiftLabel = shift === "morning" ? "AM" : "PM";
  const timing = shift === "morning" ? "1600-2030" : "2300-0100";
  // The leading title line is optional — it's discarded (overwritten by the real
  // deployment header below it) rather than parsed, but showing it in the example
  // matches how a copy-pasted ops room message usually starts.
  return `${modeLabel} SOS ${shiftLabel}\n05 x bus (${timing})\n- officer A\n- officer B\n- officer C\n- officer D\n- officer E`;
}

// ---------------------------------------------------------------------------
// Add OT Tab — preset options
// ---------------------------------------------------------------------------

export interface OTPreset {
  label: string;
  start: string;
  end: string;
}

export const OT_PRESETS_MORNING: OTPreset[] = [ //label inludes 30 minutes buffer front and back
  { label: "1130-1530 (11-4pm)", start: "1130", end: "1530" }, 
  { label: "1630-2030 (4-9pm)", start: "1630", end: "2030" },
];

export const OT_PRESETS_NIGHT: OTPreset[] = [
  { label: "0630-1000 (6-11am)", start: "0630", end: "1000" }
];
